export type UiComponentCategory =
  | 'Feedback'
  | 'Inputs'
  | 'Navigation'
  | 'Data Display'
  | 'Charting'
  | 'Custom components'

export type UiFoundationEntry = {
  id: string
  label: string
  description: string
}

export type UiComponentEntry = {
  id: string
  label: string
  category: UiComponentCategory
  description: string
}

export const UI_FOUNDATION_REGISTRY: UiFoundationEntry[] = [
  {
    id: 'color-palette',
    label: 'Color palette',
    description:
      'Core color families and shades used across the design system.',
  },
  {
    id: 'typography',
    label: 'Typography',
    description:
      'Core type styles, weights, and scales used across the design system.',
  },
  {
    id: 'icons',
    label: 'Icons',
    description: 'System icons, sizes, and usage examples.',
  },
]

export const UI_COMPONENT_REGISTRY: UiComponentEntry[] = [
  // Feedback
  {
    id: 'alert',
    label: 'Alert',
    category: 'Feedback',
    description:
      'Displays important notifications that may require user action.',
  },
  {
    id: 'modal',
    label: 'Modal',
    category: 'Feedback',
    description:
      'Centered dialog with header, scrollable body, and footer actions across four widths and a confirmation layout.',
  },
  {
    id: 'overlay',
    label: 'Overlay',
    category: 'Feedback',
    description:
      'Semi-transparent backdrop that dims page content behind modals, drawers, and dialogs.',
  },
  {
    id: 'progress-bar',
    label: 'Progress bar',
    category: 'Feedback',
    description:
      'Pill-shaped linear progress indicator with a centered percentage label for default, partial, and filled states.',
  },
  {
    id: 'toast',
    label: 'Toast',
    category: 'Feedback',
    description:
      'Compact status chips for info, success, warning, and danger — 8px padding, 16px icon, pastel fill and matching border.',
  },
  {
    id: 'tooltip',
    label: 'Tooltip',
    category: 'Feedback',
    description:
      'Compact contextual label with directional arrow for supplementary information on hover or focus.',
  },

  // Inputs
  {
    id: 'autocomplete',
    label: 'Autocomplete',
    category: 'Inputs',
    description:
      'Predictive search field with filtered suggestions and match highlighting in the dropdown.',
  },
  {
    id: 'button',
    label: 'Button',
    category: 'Inputs',
    description:
      'Action control with primary, secondary, and tertiary hierarchies, four sizes, and full state coverage.',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    category: 'Inputs',
    description:
      'Date, range, month, and year pickers with navigation and selection styling.',
  },
  {
    id: 'checkbox',
    label: 'Checkbox',
    category: 'Inputs',
    description:
      'Selection control with checked, unchecked, and indeterminate values plus full interactive states.',
  },
  {
    id: 'color-select',
    label: 'Color select',
    category: 'Inputs',
    description:
      'Select a design-system color family from a swatch list, matching Select field chrome.',
  },
  {
    id: 'combobox',
    label: 'Combobox',
    category: 'Inputs',
    description:
      'Type-or-select field that combines an input with a filterable dropdown, matching Input field and Select styling.',
  },
  {
    id: 'input-field',
    label: 'Input field',
    category: 'Inputs',
    description:
      'Text inputs with prefixes, icons, tags, OTP cells, and helper text across interactive and validation states.',
  },
  {
    id: 'input-with-unit',
    label: 'Input with unit',
    category: 'Inputs',
    description:
      'Input field with a unit selector dropdown, matching Input field styling.',
  },
  {
    id: 'link-button',
    label: 'Link button',
    category: 'Inputs',
    description:
      'Plain-text action styled like a hyperlink for low-emphasis inline actions.',
  },
  {
    id: 'multiselect-combobox',
    label: 'Multiselect combobox',
    category: 'Inputs',
    description:
      'Type-to-filter field that keeps every selection as a removable chip inside the control.',
  },
  // Radio hidden from the DS list for now — showcase kept for later.
  // {
  //   id: 'radio',
  //   label: 'Radio',
  //   category: 'Inputs',
  //   description:
  //     'Single-select control for mutually exclusive choices, with grouped and standalone usage.',
  // },
  {
    id: 'selection-box',
    label: 'Selection box',
    category: 'Inputs',
    description:
      'Radio-style option card with title, description, and Selected label. Not the circular Radio or Selectable card.',
  },
  {
    id: 'search',
    label: 'Search',
    category: 'Inputs',
    description:
      'Compact search input with hover, focus, filled, disabled, and filter variants.',
  },
  {
    id: 'select',
    label: 'Select',
    category: 'Inputs',
    description:
      'Dropdown field with label, optional tooltip, helper text, icon support, an optional text button beside the arrow, and single or multi-select menus.',
  },
  {
    id: 'select-chip',
    label: 'Select chip',
    category: 'Inputs',
    description:
      'Compact chip trigger (+ label / count) with a single-select checkmark menu, distinct from the form Select.',
  },
  {
    id: 'toggle-switch',
    label: 'Toggle switch',
    category: 'Inputs',
    description:
      'Binary on/off control with default, hover, focus, and disabled states.',
  },

  // Navigation
  {
    id: 'accordion',
    label: 'Accordion',
    category: 'Navigation',
    description:
      'Expandable sections for organizing content with optional info icons.',
  },
  {
    id: 'breadcrumbs',
    label: 'Breadcrumbs',
    category: 'Navigation',
    description:
      'Shows the current page location within a hierarchy with optional collapse.',
  },
  {
    id: 'contextual-menu',
    label: 'Contextual menu',
    category: 'Navigation',
    description:
      'Compact kebab trigger that opens a dropdown list of contextual actions.',
  },
  {
    id: 'drawer',
    label: 'Drawer',
    category: 'Navigation',
    description:
      'Collapsible navigation rail with icon-only and expanded modes in light and color modes.',
  },
  {
    id: 'stepper',
    label: 'Stepper',
    category: 'Navigation',
    description:
      'Horizontal and vertical progress indicators for multi-step forms and wizards with inactive, active, and completed states.',
  },
  {
    id: 'tabs',
    label: 'Tabs',
    category: 'Navigation',
    description:
      'Segmented horizontal tabs and stacked vertical tabs for switching related content within a page.',
  },

  // Data Display
  {
    id: 'action-buttons',
    label: 'Action buttons',
    category: 'Data Display',
    description:
      'Compact icon buttons for row-level actions (view, edit, delete) with an optional overflow menu.',
  },
  {
    id: 'avatar',
    label: 'Avatar',
    category: 'Data Display',
    description:
      'User avatar with initials, icon, or image in light and color modes.',
  },
  {
    id: 'badge',
    label: 'Badge',
    category: 'Data Display',
    description:
      'Compact labels for status, tags, and filters in solid or tinted colors.',
  },
  {
    id: 'data-table',
    label: 'Data table',
    category: 'Data Display',
    description:
      'Tabular data with sortable headers, row selection, actions, pagination, and status states.',
  },
  {
    id: 'empty-state',
    label: 'Empty state',
    category: 'Data Display',
    description:
      'Placeholder UI for empty lists, tables, and panels when there is no data to display.',
  },
  {
    id: 'pagination',
    label: 'Pagination',
    category: 'Data Display',
    description:
      'Page navigation with results-per-page selector, arrow controls, and ellipsis for long page ranges.',
  },

  // Charting
  {
    id: 'area-chart',
    label: 'Area chart',
    category: 'Charting',
    description:
      'Linear area series with cyan-400 stroke and cyan-100 fill at 40% opacity.',
  },
  {
    id: 'bar-chart',
    label: 'Bar chart',
    category: 'Charting',
    description:
      'Vertical and horizontal bar primitives — solid or stacked fills with axis / value labels.',
  },
  {
    id: 'donut-chart',
    label: 'Donut chart',
    category: 'Charting',
    description:
      'Progress ring with rounded caps, centered title and value — blue-300 track and blue-500 arc.',
  },
  {
    id: 'line-chart',
    label: 'Line chart',
    category: 'Charting',
    description:
      'Stroke-only series with sample X/Y axes and legend — companion to Area chart.',
  },
  {
    id: 'pie-chart',
    label: 'Pie chart',
    category: 'Charting',
    description:
      'Full circular segments in the design-system blue scale for reusable charting.',
  },
  {
    id: 'smooth-area-chart',
    label: 'Smooth area chart',
    category: 'Charting',
    description:
      'Curved area series with blue-500 stroke and blue-500 fill at 10% opacity.',
  },

  // Custom components — product / domain composites
  {
    id: 'audio-player',
    label: 'Audio player',
    category: 'Custom components',
    description:
      'Compact call-recording player with skip, play/pause, scrubber, volume, and speed — as used in Messages Call logs.',
  },
  {
    id: 'charting-tabs',
    label: 'Charting tabs',
    category: 'Custom components',
    description:
      'Charting-specific segmented tabs with a filled track and raised selected segment, including light and modes.',
  },
  {
    id: 'day-time-selector',
    label: 'Day time selector',
    category: 'Custom components',
    description:
      'Preferred days (Working days) plus Between/Till time range in a select-style panel, as used in Add Patient.',
  },
  {
    id: 'description-list',
    label: 'Description list',
    category: 'Custom components',
    description:
      'Label/value field rows — Claims Transaction info (panel) and Files → File Details (file-details) with dashed connectors.',
  },
  {
    id: 'detail-card',
    label: 'Detail card',
    category: 'Custom components',
    description:
      'Title plus label/value rows for plan limits and similar summaries — plain or tinted, as in Insurance Summary Individual / Family cards.',
  },
  {
    id: 'file-actions',
    label: 'File actions',
    category: 'Custom components',
    description:
      'Selection toolbar for Files — close, (x) Selected, download, move, copy, and delete.',
  },
  {
    id: 'header',
    label: 'Header',
    category: 'Custom components',
    description:
      'Application top bar with brand mark, date, search, HIPAA view toggle, location, notifications, and account menu.',
  },
  {
    id: 'mailbox-nav',
    label: 'Mailbox nav',
    category: 'Custom components',
    description:
      'Message Center left rail with system mailboxes, counts, dividers, and collapsible Folders / Labels — as used in Fax and Email.',
  },
  {
    id: 'metric-card',
    label: 'Metric card',
    category: 'Custom components',
    description:
      'Metric / KPI tile with label, value, optional unit, delta badge, and footer trend for Dashboard, Reports, and Marketing.',
  },
  {
    id: 'nav-list',
    label: 'Nav list',
    category: 'Custom components',
    description:
      'Vertical selectable list with optional section title and chevron — used for Networks in Insurance Summary and similar master-detail panels.',
  },
  {
    id: 'rich-text-editor',
    label: 'Rich text editor',
    category: 'Custom components',
    description:
      'Composed message editor with formatting toolbar and footer actions for Email/Fax and SMS/Notes flows.',
  },
  {
    id: 'schedule-card',
    label: 'Schedule card',
    category: 'Custom components',
    description:
      'Appointment calendar card for Day/Week views. Color follows the assigned provider palette.',
  },
  {
    id: 'scheduler',
    label: 'Scheduler',
    category: 'Custom components',
    description:
      'Appointment day board with room columns, time grid, Schedule cards, and Appointment Info. DS path to replace Syncfusion calendar views.',
  },
  {
    id: 'screen-loader',
    label: 'Screen loader',
    category: 'Custom components',
    description:
      'Full-screen loading overlay with the animated Enamel mark, used while Patient Management and other modules load.',
  },
  {
    id: 'section-card',
    label: 'Section card',
    category: 'Custom components',
    description:
      'Container shell for dashboard sections and report chart panels with title, optional link/actions, and content body.',
  },
  {
    id: 'selectable-card',
    label: 'Selectable card',
    category: 'Custom components',
    description:
      'Catalog card for Favourites & Bundles grids — select or manage modes with optional tags, meta, and actions.',
  },
  {
    id: 'selectable-list',
    label: 'Selectable list',
    category: 'Custom components',
    description:
      'Master list with header meta and rich rows (primary, secondary, amount, status) — as used for Claim list in Claims Transaction info.',
  },
  {
    id: 'sort-filter',
    label: 'Sort + filter',
    category: 'Custom components',
    description:
      'Icon trigger with sort (single-select sections) or filter (checkbox multi-select) dropdown — as used in Files browse headers.',
  },
  {
    id: 'work-schedule',
    label: 'Work schedule',
    category: 'Custom components',
    description:
      'Weekly staff schedule grid with shift, day-off, half-day, and override cells — as used in Settings → Team → Work schedule.',
  },
  {
    id: 'working-days',
    label: 'Working days',
    category: 'Custom components',
    description:
      'Week strip of M–S day indicators for provider availability (available / unavailable), including interactive toggle and mode.',
  },
]

export const UI_COMPONENT_CATEGORIES: UiComponentCategory[] = [
  'Feedback',
  'Inputs',
  'Navigation',
  'Data Display',
  'Charting',
  'Custom components',
]
